import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AnchorAsButton from "../../components/common/AnchorAsButton/AnchorAsButton";
import Button from "../../components/common/Button/Button";
import FontSizeSwapper from "../../components/common/FontSizeSwapper/FontSizeSwapper";
import Modal from "../../components/common/Modal/Modal";
import Toc from "../../components/common/Toc/Toc";
import ToHome from "../../components/common/ToHome/ToHome";
import { setNextActiveMenuItem, setPrevActiveMenuItem } from "../../redux/actions/menu/menu";
import { setSaving } from "../../redux/actions/saveData/saveData";
import { selectMenu } from "../../redux/selectors/exportSelectorPaths";
import MenuItem from "../../types/menu/MenuItem";
import { useNavigation } from "../../utils/context/NavigationContext";
import { useReadOnly } from "../../utils/ReadOnlyContext";
import './Footer.css';

function Footer(): JSX.Element {
    const dispatch = useDispatch();
    const { setDirection } = useNavigation();
    const { isReadOnly } = useReadOnly();
    const { i18n } = useTranslation();
    const { t } = useTranslation("common");
    const menuState = useSelector(selectMenu);
    const [orderedMenu, setOrderedMenu] = useState([] as Array<MenuItem>);
    const [showTocModal, setShowTocModal] = useState(false)

    const indexOf = (activeMenuItem: MenuItem) => {
        for (let i = 0; i < orderedMenu.length; i++) {
            if (activeMenuItem.id === orderedMenu[i].id) {
                const selectedTabIndex = activeMenuItem.selectedTabIndex;
                if (selectedTabIndex !== null && selectedTabIndex !== undefined) {
                    if (selectedTabIndex !== orderedMenu[i].selectedTabIndex) {
                        continue;
                    }
                    return i;
                }
                return i;
            }
        }
        return -1;
    }

    const activeMenuItemIndex = indexOf(menuState.activeItem)

    const handleNext = () => {
        if (document.activeElement && ["back-button", "next-button", "save-button"].includes(document.activeElement.id)) {
            (document.activeElement as HTMLElement).blur()
        }
        if (activeMenuItemIndex === orderedMenu.length - 1) {
            return;
        }

        let newIndex = activeMenuItemIndex;
        do {
            newIndex++;
            // if (orderedMenu[newIndex].componentName) {
            dispatch(setNextActiveMenuItem(orderedMenu[newIndex]));
            setDirection('forward');
            break;
            // }
        } while (newIndex < orderedMenu.length);
    };

    const handleBack = () => {
        if (document.activeElement && ["back-button", "next-button", "save-button"].includes(document.activeElement.id)) {
            (document.activeElement as HTMLElement).blur()
        }
        if (activeMenuItemIndex < 1) {
            return;
        }

        let newIndex = activeMenuItemIndex;
        do {
            newIndex--;
            // if (orderedMenu[newIndex].componentName) {
            dispatch(setPrevActiveMenuItem(orderedMenu[newIndex]));
            setDirection('backward');
            break;
            // }
        } while (newIndex > 0);
    };

    const handleSave = () => {
        if (document.activeElement && ["back-button", "next-button", "save-button"].includes(document.activeElement.id)) {
            (document.activeElement as HTMLElement).blur()
        }
        setDirection('forward');
        dispatch(setSaving());
    };

    useEffect(() => {
        if (menuState.menu && menuState.menu.length > 0) {
            const om = [] as Array<MenuItem>;
            const sort = (root: Array<MenuItem> | null | undefined) => {
                if (root && root.length > 0) {
                    for (let i = 0; i < root.length; i++) {
                        if (root[i].componentName) {
                            // if (root[i].hasTabs && root[i].subItems) {
                            //     // om.push({...root[i], selectedTabIndex: 0});
                            //     for (let j = 0; j < (root[i].subItems as MenuItem[]).length; j++) {
                            //         om.push({ ...root[i], selectedTabIndex: j });
                            //     }
                            //     continue;
                            // }
                            om.push(root[i]);
                        }
                        sort(root[i].subItems);
                    }
                }
                return;
            };
            sort(menuState.menu);
            setOrderedMenu(om);
        }
    }, [menuState.menu]);

    return (
        <footer className="footer-root">
            <div className="footer-links-container">
                <div className="footer-text">
                    <FontSizeSwapper />
                </div>
                <div className="footer-text">
                    <AnchorAsButton onClick={() => window.open(t("privacy-link"), "_blank", "noreferrer")}>
                        {t("privacy-policy")}
                    </AnchorAsButton>
                </div>
                <div>
                    <AnchorAsButton onClick={() => setShowTocModal(true)}>
                        {t("terms-and-conditions")}
                    </AnchorAsButton>
                </div>
                <div className="footer-text all-rights-reserved">
                    <AnchorAsButton>
                        {t("all-rights-reserved", { activeCampaignYear: process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR })}
                    </AnchorAsButton>
                </div>
                {showTocModal &&
                    <Modal
                        onCloseModal={() => setShowTocModal(false)}
                        title={t("title", { ns: "toc" })}>
                        <Toc />
                    </Modal>
                }
            </div>
            {
                (window.location.href.includes("#/portal") && activeMenuItemIndex >= 0) &&
                <div className="footer-button-holder">
                    {activeMenuItemIndex > 0 &&
                        <Button onClick={() => handleBack()}>
                            {t("back").toLocaleUpperCase(i18n.language)}
                        </Button>
                    }
                    {(activeMenuItemIndex === (orderedMenu.length - 1)) ?
                        <ToHome>
                            <Button>
                                {t("home").toLocaleUpperCase(i18n.language)}
                            </Button>
                        </ToHome>
                        :
                        <Button onClick={() => handleNext()}>
                            {t("next").toLocaleUpperCase(i18n.language)}
                        </Button>
                    }
                    {(!isReadOnly && !(["submit", "summaryReview", "agsSummary"].includes(menuState.activeItem.name))) &&
                        <Button onClick={() => handleSave()}>
                            {t("save").toLocaleUpperCase(i18n.language)}
                        </Button>
                    }
                </div >
            }
        </footer >
    );
}

export default Footer;
